/**
 * Settings → Context → Linked repositories (ACT2-1133). One row per project — a repository and the
 * folder its project file lives in — with the account keys it builds docs for. The one place repo links
 * are made and changed (owner decision 2026-10-27): a link is keyed by database, not by account, so it
 * outlives the account, and an account's own sheet cannot show a link whose account is gone.
 */
import { useQuery } from '@tanstack/react-query';
import { GitBranch, Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { ROW_ACTIONS_COLUMN } from '@/_shared/components/list/listColumns';
import { ListNoMatches } from '@/_shared/components/list/ListStates';
import { ListToolbar } from '@/_shared/components/list/ListToolbar';
import { Table, type TableColumn } from '@/_shared/components/Table';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/_shared/components/ui/empty';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useListControls } from '@/_shared/hooks/useListControls';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { filterSortItems, type SortState } from '@/_shared/lib/list-controls';

import { EditProjectDialog } from '../components/linkedRepos/EditProjectDialog';
import {
  AccountKeysCell,
  LinkedRepoActionsCell,
  LinkedRepoStatusCell,
  RepositoryCell,
  RepositoryLink,
} from '../components/linkedRepos/LinkedRepoActionsCell';
import { LinkRepositoryDialog } from '../components/linkedRepos/LinkRepositoryDialog';
import { ProjectDrawer } from '../components/linkedRepos/ProjectDrawer';
import { RemoveKeyDialog, type RemoveKeyTarget } from '../components/linkedRepos/RemoveKeyDialog';
import { UnlinkRepositoryDialog } from '../components/linkedRepos/UnlinkRepositoryDialog';
import { SettingsSectionHeader } from '../components/SettingsSectionHeader';
import { nameWarehouseAccounts } from '../components/warehouseAccount/useWarehouseAccounts';
import {
  groupLinkedProjects,
  LINKED_REPO_DEFAULT_SORT,
  LINKED_REPO_DERIVE_CONFIG,
  LINKED_REPO_FILTER_KEYS,
  LINKED_REPO_SORT_KEYS,
  LINKED_REPO_SORT_OPTIONS,
  type LinkedProject,
  type LinkedProjectKey,
  linkedRepoFilterGroups,
  type LinkedRepoFilterKey,
  type LinkedRepoSortKey,
  projectPathLabel,
} from '../lib/linkedRepoList';

export const ADMINS_ONLY_LINK = 'Only workspace admins can link repositories';

const DESCRIPTION =
  "Repositories Lookout builds docs from, for each account key (warehouse database) they're linked to.";

export function SettingsLinkedRepos(): React.ReactElement {
  const orpc = useOrpc();
  const { isAdmin } = useWorkspace();

  const projectsQuery = useQuery({
    ...orpc.warehouseProjects.list.queryOptions({ input: {} }),
    // Follow a compile until it settles, so the row's "Generating docs" goes away on its own.
    refetchInterval: (query) =>
      query.state.data?.projects.some((p) => p.docState?.status === 'generating') ? 5000 : false,
  });
  const accountsQuery = useQuery(orpc.settings.warehouses.listAccounts.queryOptions());
  const reposQuery = useQuery(orpc.settings.scm.listRepos.queryOptions());

  const accounts = useMemo(
    () =>
      nameWarehouseAccounts(accountsQuery.data?.accounts ?? []).map((a) => ({
        id: a.id,
        accountKey: a.accountKey,
        name: a.name,
        type: a.type,
      })),
    [accountsQuery.data?.accounts],
  );
  const projects = useMemo(
    () => groupLinkedProjects(projectsQuery.data?.projects ?? [], accounts),
    [projectsQuery.data?.projects, accounts],
  );
  const repos = useMemo(() => reposQuery.data?.repos ?? [], [reposQuery.data?.repos]);
  const htmlUrlByRepo = useMemo(() => {
    const byRepo = new Map<string, string>();
    for (const repo of repos) {
      if (/^https:\/\//i.test(repo.htmlUrl)) byRepo.set(repo.fullName.toLowerCase(), repo.htmlUrl);
    }
    return byRepo;
  }, [repos]);
  // Only a loaded answer says GitHub is not connected: loading or a failed read is not that.
  const githubNotConnected = reposQuery.data?.githubStatus === 'none';
  /** Why Link repository is off for an admin, or null. Never a dialog with nothing to pick. */
  const linkBlocked = reposQuery.isPending
    ? 'Loading repositories from GitHub…'
    : reposQuery.isError
      ? 'Could not load repositories from GitHub. Retry above.'
      : githubNotConnected
        ? 'Connect GitHub first'
        : repos.length === 0
          ? 'No GitHub repositories are available to link. Check the GitHub connection above.'
          : null;

  const controls = useListControls<LinkedRepoSortKey, LinkedRepoFilterKey>({
    sortKeys: LINKED_REPO_SORT_KEYS,
    defaultSort: LINKED_REPO_DEFAULT_SORT,
    filterKeys: LINKED_REPO_FILTER_KEYS,
    paramPrefix: 'repos_',
  });
  const visible = useMemo(
    () =>
      filterSortItems(
        projects,
        { query: controls.query, sort: controls.sort, filters: controls.filters },
        LINKED_REPO_DERIVE_CONFIG,
      ),
    [projects, controls.query, controls.sort, controls.filters],
  );
  const filterGroups = useMemo(() => linkedRepoFilterGroups(projects), [projects]);

  const [linkOpen, setLinkOpen] = useState(false);
  // Keyed by group id, so the drawer and the Edit screen follow the project through refetches.
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [unlinkTarget, setUnlinkTarget] = useState<LinkedProject | null>(null);
  // By ids, resolved against the live list, so Remove key sends the set as it is now, not as it was on click.
  const [removeIds, setRemoveIds] = useState<{ groupId: string; accountKey: string } | null>(null);
  const drawerProject = projects.find((p) => p.groupId === drawerId) ?? null;
  const editProject = projects.find((p) => p.groupId === editId) ?? null;
  const removeTarget = useMemo((): RemoveKeyTarget | null => {
    const project = removeIds && projects.find((p) => p.groupId === removeIds.groupId);
    const key = project?.keys.find((k) => k.accountKey === removeIds?.accountKey);
    return project && key ? { project, key } : null;
  }, [projects, removeIds]);

  // Edit is last-write-wins against a concurrent edit; reading the list as it opens keeps that window short.
  const refetchProjects = projectsQuery.refetch;
  const onEdit = useCallback(
    (project: LinkedProject) => {
      setEditId(project.groupId);
      void refetchProjects();
    },
    [refetchProjects],
  );
  const onUnlink = useCallback((project: LinkedProject) => setUnlinkTarget(project), []);
  const onRemoveKey = useCallback((project: LinkedProject, key: LinkedProjectKey) => {
    // The last key is the whole project: that is Unlink, with its own confirm.
    if (project.keys.length <= 1) setUnlinkTarget(project);
    else setRemoveIds({ groupId: project.groupId, accountKey: key.accountKey });
  }, []);

  const columns = useMemo<TableColumn<LinkedProject>[]>(() => {
    const base: TableColumn<LinkedProject>[] = [
      {
        id: 'repository',
        header: 'Repository',
        activatesRow: true,
        activatesRowLabel: (p) => `Open ${p.repoFullName} (${projectPathLabel(p.projectPath)})`,
        cell: (p) => <RepositoryCell project={p} />,
        cellAside: (p) => (
          <RepositoryLink project={p} htmlUrl={htmlUrlByRepo.get(p.repoFullName.toLowerCase()) ?? null} />
        ),
      },
      {
        id: 'path',
        header: 'Path',
        width: '5.5rem',
        cell: (p) => (
          <span className="block truncate font-mono text-xs" title={projectPathLabel(p.projectPath)}>
            {projectPathLabel(p.projectPath)}
          </span>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        width: '5.5rem',
        cell: (p) => <span className="truncate">{p.projectTypeLabel}</span>,
      },
      { id: 'keys', header: 'Account keys', width: '12rem', cell: (p) => <AccountKeysCell project={p} /> },
      {
        id: 'status',
        header: 'Status',
        width: '10.5rem',
        wrap: true,
        cell: (p) => <LinkedRepoStatusCell project={p} />,
      },
    ];
    if (!isAdmin) return base;
    return [
      ...base,
      { ...ROW_ACTIONS_COLUMN, cell: (p) => <LinkedRepoActionsCell project={p} onEdit={onEdit} onUnlink={onUnlink} /> },
    ];
  }, [htmlUrlByRepo, isAdmin, onEdit, onUnlink]);

  const linkButton = (
    <ActionTooltip show={!isAdmin || linkBlocked !== null} message={isAdmin ? (linkBlocked ?? '') : ADMINS_ONLY_LINK}>
      <Button
        type="button"
        variant="secondary"
        Icon={<Plus />}
        disabled={!isAdmin || linkBlocked !== null}
        onClick={() => setLinkOpen(true)}
        label="Link repository"
      />
    </ActionTooltip>
  );

  const header = <SettingsSectionHeader title="Linked repositories" description={DESCRIPTION} action={linkButton} />;

  const loading = projectsQuery.isPending || accountsQuery.isPending;
  // The table is built from the links and the accounts only. GitHub's repository list feeds just the
  // Link repository picker (and each row's GitHub link), so its failure is said inline, not over the table.
  const failed = projectsQuery.isError || accountsQuery.isError;
  const reposFailed = isAdmin && reposQuery.isError && !failed;

  let body: React.ReactNode;
  if (failed) {
    body = (
      <Callout variant="error">
        <p>Failed to load the linked repositories.</p>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="mt-2"
          label="Retry"
          onClick={() => {
            void projectsQuery.refetch();
            void accountsQuery.refetch();
          }}
        />
      </Callout>
    );
  } else if (loading) {
    body = (
      <div role="status" aria-label="Loading linked repositories" className="flex flex-col gap-2">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-12 rounded-lg" />
        ))}
      </div>
    );
  } else if (projects.length === 0) {
    body = (
      <Empty className="border">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <GitBranch />
          </EmptyMedia>
          <EmptyTitle>{githubNotConnected ? 'Connect GitHub first' : 'No linked repositories'}</EmptyTitle>
          <EmptyDescription>
            {!githubNotConnected
              ? 'Link a repository so Lookout builds docs from its dbt, Cube, LookML, SQLMesh or Profiles project for the account keys you pick.'
              : 'Connect GitHub in the section above, then link a repository so Lookout builds docs from it.'}
          </EmptyDescription>
        </EmptyHeader>
        {isAdmin && !githubNotConnected && <EmptyContent>{linkButton}</EmptyContent>}
      </Empty>
    );
  } else {
    body = (
      <div className="flex flex-col gap-4">
        <ListToolbar
          query={controls.query}
          onQueryChange={controls.setQuery}
          searchPlaceholder="Search repositories, paths and keys…"
          sortOptions={LINKED_REPO_SORT_OPTIONS}
          sort={controls.sort}
          onSortChange={(next) => controls.setSort(next as SortState<LinkedRepoSortKey>)}
          defaultSort={LINKED_REPO_DEFAULT_SORT}
          filterGroups={filterGroups}
          filters={controls.filters}
          onFilterChange={(key, value) => controls.setFilter(key as LinkedRepoFilterKey, value)}
          onClearFilters={controls.clearFilters}
          onClearAll={controls.clearAll}
          totalCount={projects.length}
          resultCount={visible.length}
          itemNoun={{ singular: 'project', plural: 'projects' }}
        />
        {visible.length === 0 ? (
          <ListNoMatches
            noun="linked repositories"
            onClear={() => {
              controls.setQuery('');
              controls.clearFilters();
            }}
          />
        ) : (
          // One section of the Context tab, which scrolls as a page: the table keeps its own capped viewport.
          // The fixed columns add up to 37rem; below 49rem the table scrolls sideways in its box rather
          // than squeezing Repository (the only flexible column) to nothing on a phone.
          <Table<LinkedProject>
            data={visible}
            getRowId={(p) => p.groupId}
            columns={columns}
            className="[&_table]:min-w-[49rem]"
            fixedLayout
            onRowClick={(p) => setDrawerId(p.groupId)}
            selectedRowId={drawerId}
          />
        )}
      </div>
    );
  }

  return (
    <section>
      {header}
      {reposFailed && (
        <Callout variant="warning" className="mb-4">
          <p>Could not load repositories from GitHub, so you can&rsquo;t link a repository right now.</p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="mt-2"
            label="Retry"
            loading={reposQuery.isFetching}
            onClick={() => void reposQuery.refetch()}
          />
        </Callout>
      )}
      {body}
      {isAdmin && (
        <>
          <LinkRepositoryDialog
            open={linkOpen}
            onOpenChange={setLinkOpen}
            repos={repos}
            accounts={accounts}
            projects={projects}
          />
          <EditProjectDialog
            project={editProject}
            onOpenChange={(open) => !open && setEditId(null)}
            accounts={accounts}
            projects={projects}
          />
          <UnlinkRepositoryDialog
            project={unlinkTarget}
            onOpenChange={(open) => !open && setUnlinkTarget(null)}
            onUnlinked={() => setDrawerId(null)}
          />
          <RemoveKeyDialog
            target={removeTarget}
            onOpenChange={(open) => !open && setRemoveIds(null)}
            onKeysEmpty={setUnlinkTarget}
          />
        </>
      )}
      <ProjectDrawer
        project={drawerProject}
        onOpenChange={(open) => !open && setDrawerId(null)}
        isAdmin={isAdmin}
        onEdit={(p) => {
          setDrawerId(null);
          onEdit(p);
        }}
        onRemoveKey={onRemoveKey}
      />
    </section>
  );
}
